/* global console */

import type { FinsterQueryResult } from "./types";
import { FINSTER_QUERY_DATA_SOURCES } from "./constants";
import type {
  FinsterQueryDataSource,
  FinsterQueryMode,
  PublicDataSourceBrandName,
} from "./constants";
import {
  executeFinsterQueryBuffered,
  executeFinsterQueryStream,
  type FinsterStreamSubEvent,
  type PowerPointClientContext,
} from "../../../shared/api/finster/queryTool";
import {
  buildPptxLangfuseMetadata,
  type PptxLangfuseRunContext,
} from "../../langfuseTrace";

export type { FinsterStreamSubEvent };

const POWERPOINT_CLIENT_ORIGIN = "powerpoint_addin" as const;

function toFinsterQueryResult(result: {
  success: boolean;
  answer?: string;
  citations?: unknown;
  error?: string;
  sessionId?: string;
  sessionName?: string;
}): FinsterQueryResult {
  return {
    success: result.success,
    answer: result.answer,
    citations: result.citations as FinsterQueryResult["citations"],
    error: result.error,
    ...(result.sessionId ? { sessionId: result.sessionId } : {}),
    ...(result.sessionName ? { sessionName: result.sessionName } : {}),
  };
}

export async function queryFinster(
  query: string,
  dataSources?: readonly FinsterQueryDataSource[],
  publicBrandName: PublicDataSourceBrandName = "Finster",
  mode: FinsterQueryMode = "thinking",
  userFileIds?: readonly string[],
  langfuseContext?: PptxLangfuseRunContext,
  clientContext?: PowerPointClientContext,
  chatSessionId?: string,
  selectedSkillsGuidance?: string,
  dataSourceScopeIsAuthoritative: boolean = false,
): Promise<FinsterQueryResult> {
  const t0 = Date.now();
  const requestDataSources = dataSourceScopeIsAuthoritative
    ? dataSources
    : (dataSources ?? FINSTER_QUERY_DATA_SOURCES);
  console.log(`[FINSTER_QUERY] (${mode}) "${query.slice(0, 80)}..."`);

  const result = await executeFinsterQueryBuffered({
    query,
    deliveryChannel: "powerpoint",
    chatSessionId,
    clientOrigin: POWERPOINT_CLIENT_ORIGIN,
    clientContext,
    dataSources: requestDataSources,
    userFileIds,
    ...(selectedSkillsGuidance !== undefined ? { selectedSkillsGuidance } : {}),
    publicBrandName,
    mode,
    langfuseMetadata: langfuseContext
      ? buildPptxLangfuseMetadata(langfuseContext, { phase: "finster_query" })
      : undefined,
  });

  if (result.success && result.answer) {
    console.log(`[FINSTER_QUERY] ${result.answer.length} chars (${Date.now() - t0}ms)`);
  } else if (!result.success) {
    console.error(`[FINSTER_QUERY] Error (${Date.now() - t0}ms):`, result.error);
  }

  return toFinsterQueryResult(result);
}

/**
 * Streaming sibling of {@link queryFinster}. Forwards normalized sub-events to
 * `onSubEvent` for the nested chat timeline while resolving the same buffered
 * tool-result contract via the shared Finster query client.
 */
export async function queryFinsterStream(
  query: string,
  dataSources?: readonly FinsterQueryDataSource[],
  publicBrandName: PublicDataSourceBrandName = "Finster",
  mode: FinsterQueryMode = "thinking",
  onSubEvent: (event: FinsterStreamSubEvent) => void = () => {},
  signal?: AbortSignal,
  userFileIds?: readonly string[],
  langfuseContext?: PptxLangfuseRunContext,
  clientContext?: PowerPointClientContext,
  chatSessionId?: string,
  selectedSkillsGuidance?: string,
  dataSourceScopeIsAuthoritative: boolean = false,
): Promise<FinsterQueryResult> {
  const t0 = Date.now();
  const requestDataSources = dataSourceScopeIsAuthoritative
    ? dataSources
    : (dataSources ?? FINSTER_QUERY_DATA_SOURCES);
  console.log(`[FINSTER_QUERY_STREAM] (${mode}) "${query.slice(0, 80)}..."`);

  const result = await executeFinsterQueryStream({
    query,
    deliveryChannel: "powerpoint",
    chatSessionId,
    clientOrigin: POWERPOINT_CLIENT_ORIGIN,
    clientContext,
    dataSources: requestDataSources,
    userFileIds,
    ...(selectedSkillsGuidance !== undefined ? { selectedSkillsGuidance } : {}),
    publicBrandName,
    mode,
    signal,
    onSubEvent,
    langfuseMetadata: langfuseContext
      ? buildPptxLangfuseMetadata(langfuseContext, { phase: "finster_query" })
      : undefined,
  });

  if (result.success && result.answer) {
    console.log(`[FINSTER_QUERY_STREAM] ${result.answer.length} chars (${Date.now() - t0}ms)`);
  } else if (!result.success) {
    console.error(`[FINSTER_QUERY_STREAM] Error (${Date.now() - t0}ms):`, result.error);
  }

  return toFinsterQueryResult(result);
}
